import { useEffect, useState } from 'react';
import {
  stats,
  type FleetSummary,
  type Incident,
  type IngestHealth,
  type Installation,
  type Regression,
} from '../../api/stats';
import { formatDuration, formatRelative } from '../../lib/format';
import { installationPath } from '../statistics/shared';
import { pmsIdsOfEnv, useStatsLinks } from '../statistics/links';
import type { Issue } from './shared';

// Statistics: fleet tiles, silent installations, regressions and ingest health, shared by the main dashboard and the
// Statistics group dashboard.

export interface StatsSummary {
  fleet: FleetSummary;
  ingest: IngestHealth | null;
  silent: Installation[];
  regressions: Regression[];
  /** Parts that could not be read (the rest still shows). */
  errors: string[];
}

/** Backlog lag above this is worth a look (the processor drains every minute). */
export const LAG_WARN_SECONDS = 15 * 60;

async function load(): Promise<StatsSummary> {
  const errors: string[] = [];
  const soft = <T,>(p: Promise<T>, what: string, fallback: T) =>
    p.catch((e: Error) => {
      errors.push(`${what}: ${e.message}`);
      return fallback;
    });
  const [fleet, ingest, silent, regressions] = await Promise.all([
    stats.fleet(),
    soft(stats.ingest(), 'ingest health', null),
    soft(stats.installations({ status: 'silent' }), 'installations', [] as Installation[]),
    soft(stats.regressions(), 'regressions', [] as Regression[]),
  ]);
  return { fleet, ingest, silent, regressions, errors };
}

export const silentSince = (i: Installation) =>
  [i.lastSeen.performance, i.lastSeen.sys, i.lastSeen.metric].filter(Boolean).sort().pop() ?? null;

/** Everything worth a look in the statistics pipeline and fleet. */
export function statsIssues(s: StatsSummary): Issue[] {
  const out: Issue[] = [];
  const ing = s.ingest;
  if (ing?.processor.stale)
    out.push({
      key: 'stats:processor',
      status: 'down',
      name: 'Datacollector processor',
      what: 'ingest',
      detail: `not running: last state ${formatRelative(ing.processor.updatedAt)}; nothing is aggregated`,
      to: '/statistics',
    });
  if (ing?.backlog.lagSeconds != null && ing.backlog.lagSeconds > LAG_WARN_SECONDS)
    out.push({
      key: 'stats:backlog',
      status: 'warn',
      name: 'Ingest backlog',
      what: 'ingest',
      detail: `${ing.backlog.rows.toLocaleString()} rows waiting, oldest ${formatDuration(ing.backlog.lagSeconds * 1000)}`,
      to: '/statistics',
    });
  if (ing && ing.failed.last24h > 0)
    out.push({
      key: 'stats:failed',
      status: 'warn',
      name: 'Malformed performance batches',
      what: 'ingest',
      detail: `${ing.failed.last24h} dead-lettered in 24 h`,
      to: '/statistics',
    });
  for (const i of s.silent)
    out.push({
      key: `stats:silent:${i.customerId}:${i.workserver}`,
      status: 'down',
      name: `${i.name ?? `Customer ${i.customerId}`} · ${i.workserver}`,
      what: 'installation',
      detail: `silent, last data ${formatRelative(silentSince(i))}`,
      to: installationPath(i.customerId, i.workserver),
    });
  for (const r of s.regressions.slice(0, 5))
    out.push({
      key: `stats:reg:${r.customerId}:${r.workserver}:${r.method}`,
      status: 'warn',
      name: `${r.name ?? `Customer ${r.customerId}`} · ${r.workserver}`,
      what: 'regression',
      detail: `${r.method} p95 ×${r.ratio.toFixed(1)} vs 4-week baseline${r.versionChange ? ` after ${r.versionChange.to}` : ''}`,
      to: installationPath(r.customerId, r.workserver, 'regressions'),
    });
  return out;
}

const TTL = 60_000;
let cache: { at: number; data: Promise<StatsSummary> } | null = null;

/** The statistics summary (callers with ophub:stats:read), cached for a minute across dashboards. */
export function useStatsSummary(enabled: boolean) {
  const [state, setState] = useState<{ data: StatsSummary | null; loading: boolean; error: string | null }>({
    data: null,
    loading: enabled,
    error: null,
  });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    if (round > 0 || !cache || Date.now() - cache.at > TTL) cache = { at: Date.now(), data: load() };
    cache.data.then(
      (data) => alive && setState({ data, loading: false, error: null }),
      (e) => alive && setState({ data: null, loading: false, error: `Statistics: ${(e as Error).message}` })
    );
    return () => {
      alive = false;
    };
  }, [enabled, round]);
  return { ...state, enabled, reload: () => setRound((r) => r + 1) };
}

/** Test hook. */
export const resetStatsSummary = () => {
  cache = null;
};

/**
 * Operations dashboard: open statistics incidents of the registered environments (environment → AUTH tenant → its
 * customer's `pms` aliases). Only when links are available (ophub:auth:read, signed in); otherwise nothing.
 */
export function useEnvironmentIncidents(enabled: boolean): Issue[] {
  const links = useStatsLinks();
  const [open, setOpen] = useState<Incident[] | null>(null);
  const linked = links.environments.some((e) => e.authTenantId);
  useEffect(() => {
    if (!enabled || !linked) return;
    let alive = true;
    stats.incidents({ status: 'open', limit: 500 }).then(
      (p) => alive && setOpen(p.items),
      () => alive && setOpen(null)
    );
    return () => {
      alive = false;
    };
  }, [enabled, linked]);
  if (!enabled || !open?.length) return [];
  const out: Issue[] = [];
  for (const env of links.environments) {
    const ids = pmsIdsOfEnv(links, env);
    const n = open.filter((i) => i.customerId != null && ids.includes(i.customerId)).length;
    if (n)
      out.push({
        key: `stats:env:${env.id}`,
        status: 'warn',
        name: env.name,
        what: 'environment · statistics',
        detail: `${n} open performance ${n === 1 ? 'incident' : 'incidents'}`,
        to: `/statistics/incidents?customerId=${ids[0]}`,
      });
  }
  return out;
}
